import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { BookCallProvider } from "@/context/BookCall";
import { SITE, waLink } from "@/lib/data";

const PAGE_WA = {
    "/ai-agents":
        "Hi Phoebus AI — I'd like to talk about building an autonomous AI agent for our workflows.",
    "/conversational-ai":
        "Hi Phoebus AI — I'd like to discuss a conversational AI deployment for our business.",
    "/work":
        "Hi Phoebus AI — I saw your case studies and would like to discuss a similar project.",
    "/contact":
        "Hi Phoebus AI — I'd like to book a discovery call about AI automation.",
};

function WhatsAppFab() {
    const loc = useLocation();
    const text =
        PAGE_WA[loc.pathname] ||
        "Hi Phoebus AI — I'd like to discuss AI automation for our business.";
    return (
        <a
            href={waLink(text)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Chat on WhatsApp"
            data-testid="whatsapp-fab"
            className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 bg-pa-blue-600 text-white pl-4 pr-5 py-3 cut-tr shadow-lg hover:bg-pa-blue-700 transition-colors"
        >
            <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <span className="text-sm font-medium hidden sm:inline">WhatsApp</span>
        </a>
    );
}

export default function Layout() {
    const loc = useLocation();
    const lenisRef = useRef(null);

    useEffect(() => {
        const reduce = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        if (reduce) return;
        const lenis = new Lenis({ lerp: 0.1, wheelMultiplier: 1 });
        lenisRef.current = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    }, [loc.pathname]);

    // Scrollbar is invisible at rest and only fades in while a scroll is
    // actually happening (main page or any nested scroll area, e.g. a modal).
    useEffect(() => {
        let hideTimer;
        const onScroll = () => {
            document.documentElement.classList.add("is-scrolling");
            clearTimeout(hideTimer);
            hideTimer = setTimeout(() => {
                document.documentElement.classList.remove("is-scrolling");
            }, 600);
        };
        document.addEventListener("scroll", onScroll, {
            capture: true,
            passive: true,
        });
        return () => {
            document.removeEventListener("scroll", onScroll, { capture: true });
            clearTimeout(hideTimer);
        };
    }, []);

    return (
        <BookCallProvider>
            <a
                href="#main"
                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:bg-pa-blue-600 focus:text-white focus:px-4 focus:py-2"
            >
                Skip to content
            </a>
            <Header />
            <main id="main" className="pt-[72px]">
                <Outlet />
            </main>
            <Footer />
            <WhatsAppFab />
        </BookCallProvider>
    );
}
